import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

/**
 * Leseseite über die synchronisierten ILIAS-Daten.
 *
 * Bewusst getrennt von `LmsRegistryService`: dort geht es um die Verbindung
 * selbst (Zugang, Status, Platzierungen), hier nur um das, was in ILIAS
 * existiert. Dieselbe Trennung wie zwischen `MoodleDashboardService` und der
 * Placements-Karte.
 */

export interface IliasSyncSettings {
  enabled: boolean;
  /** `placements` = nur Kurse mit Agent, `all` = der ganze Baum. */
  mode: 'placements' | 'all';
  root_ref_id?: number | null;
  next_due_at?: string | null;
  last_run_at?: string | null;
  consecutive_fails?: number;
  last_error?: string | null;
  /** Inhalts-Sync: Kursdateien in die Wissensbasis. Getrennt vom
   *  Metadaten-Sync, weil er Speicher und — bei Videos — Transkription kostet. */
  content_enabled?: boolean;
  content_mode?: 'placements' | 'all';
  /** Klarnamen und E-Mails speichern. Aus ist die Vorgabe — ohne den Schalter
   *  bleibt nur die ILIAS-usr_id. */
  pii_enabled?: boolean;
  pii_last_synced_at?: string | null;
  /** Didaktische Analyse nach dem Inhalts-Sync (Kapitel, Lernziele). */
  structure_parse?: boolean;
}

/** Was der Admin in der Kachel wählt. `off` heisst nicht „nie", sondern
 *  „nur was je Platzierung gesetzt ist" — wie bei Moodle. */
export type IliasContentScope = 'off' | 'placements' | 'all';

export interface IliasConnection {
  id: string;
  display_name: string | null;
  environment: string | null;
  issuer: string | null;
  platform_version: string | null;
  /** Hauptversion für den Hinweis auf zu alte Installationen. */
  platform_major: number | null;
  soap_configured: boolean;
  /** `lti13` oder `ilias_plugin`. */
  source?: string;
  /** Hat diese Verbindung einen LTI-Konsumenten? Eine reine
   *  Plugin-Installation hat keinen. Ohne diese Angabe schloss der Dialog
   *  vom Zustandswort auf den Kanal — was nicht geht, weil dasselbe Wort
   *  für beide Kanäle steht. */
  lti_linked?: boolean;
  /** `connected` | `paused` | `disconnected` | … — je Kanal abgeleitet. */
  connection_status: string;
  paused: boolean;
  auto_sync: IliasSyncSettings;
  /** Zustand des ILIAS-Plugins, sofern gekoppelt. Hängt an der Verbindung,
   *  nicht an ihrer Quelle: eine per LTI entstandene Verbindung kann
   *  zusätzlich gekoppelt sein. Ist sie es, sind die zehn SOAP-Schritte
   *  gegenstandslos und der Dialog zeigt sie nicht mehr. */
  plugin?: IliasPluginState | null;
}

export interface IliasPluginState {
  paired: boolean;
  site_identifier: string | null;
  site_url: string | null;
  version: string | null;
  /** Monotoner Datumscode. Der einzige zulässige Maßstab für „kann das
   *  schon" — nach dem Release-String zu sortieren ginge daneben. */
  version_code: number | null;
  ilias_version: string | null;
  /** Was das Plugin selbst meldet: objects, members, progress, … */
  capabilities: string[];
  paired_at: string | null;
  last_seen_at: string | null;
}

export interface IliasCourseRow {
  registration_id: string;
  /** ILIAS ref_id — die Zahl, die auch der Launch als Kontext nennt. */
  ref_id: number;
  obj_id: number | null;
  object_type: string;
  title: string | null;
  path: string | null;
  member_count: number;
  teacher_count: number;
  /** Wie viele Agent-Platzierungen auf diesen Kurs zeigen. */
  placement_count: number;
  /** Abgegebene Testergebnisse in diesem Kurs. */
  result_count: number;
  /** Unser eigener Kurs hinter dem ILIAS-Objekt, aufgelöst über die
   *  Platzierung. `null`, solange keiner existiert — dann hängt an diesem
   *  Objekt noch kein Agent und keine Inhalte, und alles, was `courses.id`
   *  adressiert (Verknüpfungen, Verantwortliche), geht noch nicht. */
  course_id: string | null;
  last_synced_at: string | null;
}

export interface IliasUserRow {
  registration_id: string;
  usr_id: number;
  login: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  active: boolean;
  learner_user_id: string | null;
  enrollment_count: number;
  completed_count: number;
  last_progress_at: string | null;
  /** Testergebnisse dieser Person. */
  test_count: number;
  test_passed: number;
  /** Durchschnittliche Punktzahl in Prozent; null, solange nichts vorliegt. */
  avg_percentage: number | null;
  last_synced_at: string | null;
}

export interface IliasTeacherCourse {
  ref_id: number;
  title: string | null;
  path: string | null;
  role: string;
}

export interface IliasTeacherRow {
  registration_id: string;
  usr_id: number;
  login: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  roles: string[];
  course_count: number;
  courses: IliasTeacherCourse[];
  last_synced_at: string | null;
}

export interface IliasPage<T> {
  total: number;
  rows: T[];
}

export interface IliasFullSyncResult {
  mode?: string | null;
  tree?: { objects: number; courses: number; created: number; updated: number } | null;
  members?: { containers: number; users: number; memberships: number; failed: number } | null;
  progress?: { changes: number; applied: number } | null;
  results?: { tests: number; results: number; failed: number } | null;
}

@Injectable({ providedIn: 'root' })
export class IliasOverviewService {
  private base = `${environment.tutorsBackend}/ilias/overview`;
  private registryBase = `${environment.tutorsBackend}/lms/registrations`;

  constructor(private http: HttpClient) {}

  /** Die ILIAS-Verbindungen des Mandanten. Mehrere sind möglich — ein Kunde
   *  kann Test- und Produktivsystem getrennt verbunden haben. */
  listConnections(): Observable<IliasConnection[]> {
    return this.http.get<IliasConnection[]>(`${this.base}/registrations`);
  }

  listCourses(opts: {
    registrationId?: string | null;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Observable<IliasPage<IliasCourseRow>> {
    return this.http.get<IliasPage<IliasCourseRow>>(`${this.base}/courses`, {
      params: this.buildParams(opts),
    });
  }

  listUsers(opts: {
    registrationId?: string | null;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Observable<IliasPage<IliasUserRow>> {
    return this.http.get<IliasPage<IliasUserRow>>(`${this.base}/users`, {
      params: this.buildParams(opts),
    });
  }

  listTeachers(opts: {
    registrationId?: string | null;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Observable<IliasPage<IliasTeacherRow>> {
    return this.http.get<IliasPage<IliasTeacherRow>>(`${this.base}/teachers`, {
      params: this.buildParams(opts),
    });
  }

  getSyncSettings(registrationId: string): Observable<IliasSyncSettings> {
    return this.http.get<IliasSyncSettings>(
      `${this.registryBase}/${registrationId}/ilias/auto-sync`,
    );
  }

  saveSyncSettings(
    registrationId: string,
    payload: Partial<Pick<IliasSyncSettings, 'enabled' | 'mode' | 'root_ref_id' | 'pii_enabled'>>,
  ): Observable<IliasSyncSettings> {
    return this.http.put<IliasSyncSettings>(
      `${this.registryBase}/${registrationId}/ilias/auto-sync`,
      payload,
    );
  }

  /** Inhalts-Sync und didaktische Analyse schalten. */
  saveContentSync(
    registrationId: string,
    payload: { enabled?: boolean; mode?: 'placements' | 'all'; structure_parse?: boolean },
  ): Observable<IliasSyncSettings> {
    return this.http.put<IliasSyncSettings>(
      `${this.registryBase}/${registrationId}/ilias/content-sync`,
      payload,
    );
  }

  /** Ein vollständiger Lauf von Hand — dieselbe Funktion, die auch der
   *  Hintergrund-Worker ausführt. */
  runSync(registrationId: string): Observable<IliasFullSyncResult> {
    return this.http.post<IliasFullSyncResult>(
      `${this.registryBase}/${registrationId}/ilias/sync`,
      {},
    );
  }

  private buildParams(opts: {
    registrationId?: string | null;
    search?: string;
    limit?: number;
    offset?: number;
  }): HttpParams {
    let params = new HttpParams();
    if (opts.registrationId) params = params.set('registration_id', opts.registrationId);
    if (opts.search?.trim()) params = params.set('search', opts.search.trim());
    if (opts.limit != null) params = params.set('limit', String(opts.limit));
    if (opts.offset != null) params = params.set('offset', String(opts.offset));
    return params;
  }
}
